import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Button } from "./button";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export type DialogPresentation = "compact" | "form" | "workflow" | "media";

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn("ui-dialog-overlay", className)} {...props} />
));

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    leadingClose?: boolean;
    overlayClassName?: string;
    presentation?: DialogPresentation;
    showClose?: boolean;
  }
>(({ className, children, leadingClose = false, overlayClassName, presentation, showClose = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "ui-dialog-content",
        leadingClose && "ui-dialog-leading-close",
        presentation && `ui-dialog-content-${presentation}`,
        className
      )}
      {...props}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close asChild>
          <Button variant="secondary" size="icon" className="ui-dialog-close" type="button" aria-label="Close">
            <X size={20} />
          </Button>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));

DialogContent.displayName = DialogPrimitive.Content.displayName;

export function DialogHeader({ className, ...props }: ComponentPropsWithoutRef<"div">) {
  return <div className={cn("ui-dialog-header", className)} {...props} />;
}

export const DialogBody = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div">
>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("ui-dialog-body", className)}
      {...props}
    />
  )
);

DialogBody.displayName = "DialogBody";

export function DialogFooter({ className, ...props }: ComponentPropsWithoutRef<"div">) {
  return <div className={cn("ui-dialog-footer", className)} {...props} />;
}

export function DialogTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("ui-dialog-title-block", className)}>
      <DialogPrimitive.Title>{children}</DialogPrimitive.Title>
    </div>
  );
}

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("ui-dialog-description", className)} {...props} />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;
